// The dashboard calls the signed-in organisation a "chamber" by default. A
// chamber whose own name contains "Board of Trade" is shown as a "board of
// trade" instead. The noun comes only from the chamber name: there is no
// stored setting, and backend emails, invoice PDFs, the website and the app
// keep their own wording.

export type CmpOrganizationNoun = {
  isBoardOfTrade: boolean;
  /** Mid-sentence singular: "chamber" | "board of trade". */
  singular: string;
  /** Title case singular: "Chamber" | "Board of Trade". */
  Singular: string;
  /** Mid-sentence plural: "chambers" | "boards of trade". */
  plural: string;
  /** Title case plural: "Chambers" | "Boards of Trade". */
  Plural: string;
  /** Mid-sentence possessive: "chamber's" | "board of trade's". */
  possessive: string;
  /** Title case possessive: "Chamber's" | "Board of Trade's". */
  Possessive: string;
  /** Indefinite article for the singular noun. Both nouns take "a". */
  article: "a";
};

const CHAMBER_NOUN: CmpOrganizationNoun = {
  isBoardOfTrade: false,
  singular: "chamber",
  Singular: "Chamber",
  plural: "chambers",
  Plural: "Chambers",
  possessive: "chamber's",
  Possessive: "Chamber's",
  article: "a",
};

const BOARD_OF_TRADE_NOUN: CmpOrganizationNoun = {
  isBoardOfTrade: true,
  singular: "board of trade",
  Singular: "Board of Trade",
  plural: "boards of trade",
  Plural: "Boards of Trade",
  possessive: "board of trade's",
  Possessive: "Board of Trade's",
  article: "a",
};

export const CMP_DEFAULT_ORGANIZATION_NOUN = CHAMBER_NOUN;

const BOARD_OF_TRADE_NAME_PATTERN = /\bboard\s+of\s+trade\b/i;

/**
 * Returns the organisation noun for a chamber name. The rule is "the name
 * contains Board of Trade" (case-insensitive), so a combined name such as
 * "X Chamber of Commerce and Board of Trade" also uses "board of trade".
 * A missing name (loading, logged out) uses "chamber".
 */
export const getCmpOrganizationNoun = (
  chamberName?: string | null
): CmpOrganizationNoun =>
  chamberName && BOARD_OF_TRADE_NAME_PATTERN.test(chamberName)
    ? BOARD_OF_TRADE_NOUN
    : CHAMBER_NOUN;

// Phrases that keep "Chamber" in every tenant: product and app surface names,
// the provincial or affiliated "Chamber of Commerce" proper noun, and email
// merge tags. Longer phrases come first so they win the alternation.
const PROTECTED_PHRASE_SOURCE = [
  "Chamber Member Pro",
  "Chamber Perks App™? Chamber Dashboard",
  "Chamber Perks",
  "My Chamber Tab",
  "Chamber Group Insurance",
  "Chamber Group \\/ Insurance",
  "Chamber Insurance",
  "Chamber Pro\\b",
  "One Chamber System",
  "Chambers? of Commerce",
  "\\[Chamber Name\\]",
  "\\{Chamber Logo\\}",
  "\\[Chamber Logo\\]",
].join("|");

// Group 1: the character before the word (kept). Group 2: a protected phrase.
// Group 3: the noun. Group 4: plural "s". Group 5: possessive suffix.
// The prefix/suffix checks skip identifiers, URLs, paths and email addresses
// such as "chamber-perks", "/chamber/setup" or "chamber@example.com".
const NOUN_PATTERN = new RegExp(
  `(^|[^\\w\\-/.@])(?:(${PROTECTED_PHRASE_SOURCE})|(chamber)(s)?(['’]s)?(?![\\w\\-/@]))`,
  "gi"
);

const matchCase = (source: string, lower: string, title: string): string => {
  if (source === source.toUpperCase()) return title.toUpperCase();
  return source[0] === source[0].toUpperCase() ? title : lower;
};

/**
 * Rewrites the whole word "chamber" (singular, plural, possessive) in a
 * user-facing string for a board of trade. Use it for existing module-scope
 * copy at render time. Text is returned unchanged for chambers.
 */
export const applyCmpOrganizationNoun = (
  text: string,
  noun: CmpOrganizationNoun
): string => {
  if (!noun.isBoardOfTrade || !text) return text;

  return text.replace(
    NOUN_PATTERN,
    (
      match: string,
      prefix: string,
      protectedPhrase: string | undefined,
      word: string,
      plural: string | undefined,
      possessive: string | undefined
    ) => {
      if (protectedPhrase) return match;
      const replacement = plural
        ? matchCase(word, noun.plural, noun.Plural)
        : matchCase(word, noun.singular, noun.Singular);
      return `${prefix}${replacement}${possessive ?? ""}`;
    }
  );
};

const CHAMBER_SET_UP_PAGE_NAME = "Chamber Set Up";

/**
 * Nav labels and page titles. A board of trade sees "Board Set Up" for
 * Chamber Set Up (Moeen, Oct 2026, Ajax request); "Board of Trade Set Up"
 * was too long. Other page names and body text use the normal noun rule.
 */
export const applyCmpOrganizationNounToPageName = (
  pageName: string,
  noun: CmpOrganizationNoun
): string =>
  noun.isBoardOfTrade && pageName === CHAMBER_SET_UP_PAGE_NAME
    ? "Board Set Up"
    : applyCmpOrganizationNoun(pageName, noun);

export type CmpBoardWording = {
  /** Chamber Set Up Step 5 approval checkbox label (CMP Dashboard (20) L8616). */
  approvalSettingTitle: string;
  /** Title case approvers: "Chamber or Board" | "Board of Trade or Board of Directors". */
  ApprovalBy: string;
  /** Mid-sentence approvers: "chamber and or board" | "board of trade and or Board of Directors". */
  approvalByAndOr: string;
  /** Mid-sentence board director: "chamber board director" | "board of trade director". */
  boardDirector: string;
};

/**
 * Board approval and board director wording. The chamber text is the Claude
 * Design text. Putting "Board of Trade" in the same slot would repeat "Board"
 * ("Require Board of Trade (Board of Directors) Approval", "board of trade
 * board director"), so a board of trade names its board once.
 */
export const getCmpBoardWording = (noun: CmpOrganizationNoun): CmpBoardWording =>
  noun.isBoardOfTrade
    ? {
        approvalSettingTitle:
          "New Membership Applications Require Board of Directors Approval",
        ApprovalBy: `${noun.Singular} or Board of Directors`,
        approvalByAndOr: `${noun.singular} and or Board of Directors`,
        boardDirector: `${noun.singular} director`,
      }
    : {
        approvalSettingTitle: `New Membership Applications Require ${noun.Singular} (Board of Directors) Approval`,
        ApprovalBy: `${noun.Singular} or Board`,
        approvalByAndOr: `${noun.singular} and or board`,
        boardDirector: `${noun.singular} board director`,
      };
